import { useMemo } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { useParams } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ButtonGroup,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from "@mui/material";
import { OpenInNewOff, Check as CheckIcon, RadioButtonUnchecked, PanoramaFishEye } from "@mui/icons-material";
import { RootState } from "../../configureStore";
import { ExtractTask, FormExtractions } from "../../interface/returns";
import useIsMobile from "../../hooks/useIsMobile";

interface ExtractDataHeaderProps {
    formOnly: boolean;
    setFormOnly: (value: boolean) => void;
    selectedFormType: string;
    availableFormTypes: string[];
    viewedPages: Record<string, Set<number>>;
    formPageCounts: Record<string, number>;
    removedPageCounts: Record<string, number>;
    unreviewedForms: string[];
    isFormFullyReviewed: (formType: string) => boolean;
    onFormTypeChange: (event: any) => void;
    onConfirmClick: () => void;
    onBackClick: () => void;
    onViewAllPages: () => void;
    onSaveDraftInPlace: () => void;
    onKickbackClick: () => void;
}

const ExtractDataHeader = ({
    formOnly,
    setFormOnly,
    selectedFormType,
    availableFormTypes,
    viewedPages,
    formPageCounts,
    removedPageCounts,
    unreviewedForms,
    isFormFullyReviewed,
    onFormTypeChange,
    onConfirmClick,
    onBackClick,
    onViewAllPages,
    onSaveDraftInPlace,
    onKickbackClick,
}: ExtractDataHeaderProps) => {
    const { loanId } = useParams();
    const isMobile = useIsMobile();
    const isSaving = useSelector<RootState, boolean>(({ status: { loading: l } }) => l["returns/SAVE_EXTRACTION"]);
    const hasChanges = useSelector<RootState, boolean>(({ returns }) => returns.hasChanges);
    const invalidFieldPaths = useSelector(({ returns }: RootState) => returns.invalidFieldPaths);
    const invalidFieldCount = Object.keys(invalidFieldPaths).length;
    const hasInvalidFields = invalidFieldCount > 0;
    const invalidLocations = useMemo(() => {
        const seen = new Set<string>();
        const out: { formName: string; pageNumber: number }[] = [];
        for (const info of Object.values(invalidFieldPaths)) {
            const key = `${info.formName}__${info.pageNumber}`;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(info);
        }
        return out;
    }, [invalidFieldPaths]);
    const fileName = useSelector<RootState, string | undefined>(
        ({ returns }) => returns.activeReturnTask?.type === "extract"
            ? (returns.activeReturnTask as ExtractTask).task.message.identity?.fileName
            : undefined,
    );
    const headerText = fileName ? `Extract Data - Loan ${loanId} - ${fileName}` : `Extract Data - Loan ${loanId}`;
    const reviewedFormCount = availableFormTypes.length - unreviewedForms.length;

    const unmatchedPageCounts = useSelector<RootState, Record<string, number>>(
        ({ returns }) =>
            availableFormTypes.reduce(
                (counts, formType) => {
                    const blobCount = formPageCounts[formType] || 0;
                    const extractionCount =
                        returns.extractedJson?.[formType as keyof FormExtractions]?.extraction?.length || 0;
                    return { ...counts, [formType]: Math.max(0, blobCount - extractionCount) };
                },
                {} as Record<string, number>,
            ),
        shallowEqual,
    );

    return (
        <Box
            sx={{
                p: isMobile ? 1 : 2,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: 0.5,
                flexWrap: isMobile ? "wrap" : "nowrap",
                gap: isMobile ? 1 : 0,
            }}
        >
            <Tooltip title={headerText}>
                <Typography
                    variant={isMobile ? "subtitle1" : "h5"}
                    sx={{
                        fontWeight: 600,
                        flex: "1 1 auto",
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        ...(isMobile && {
                            maxWidth: "60vw",
                        }),
                    }}
                >
                    {headerText}
                </Typography>
            </Tooltip>
            <Box
                sx={{
                    display: "flex",
                    gap: isMobile ? 1 : 2,
                    alignItems: "center",
                    flexWrap: isMobile ? "wrap" : "nowrap",
                    flexShrink: 0,
                    justifyContent: "flex-end",
                    ...(isMobile && { width: "100%" }),
                }}
            >
                {formOnly && !isMobile && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <ButtonGroup
                            size="small"
                            variant="outlined"
                        >
                            <Button
                                onClick={() => {
                                    setFormOnly(false);
                                }}
                                title="Show PDF"
                            >
                                <OpenInNewOff fontSize="small" />
                            </Button>
                        </ButtonGroup>
                    </Box>
                )}
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mr: 1 }}
                >
                    {reviewedFormCount}/{availableFormTypes.length} forms reviewed
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
                    {hasInvalidFields && (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                            {invalidLocations.map((loc) => (
                                <Typography
                                    key={`${loc.formName}__${loc.pageNumber}`}
                                    variant="caption"
                                    color="error"
                                    sx={{ lineHeight: 1.2, fontWeight: 600 }}
                                >
                                    Error on {loc.formName} on page {loc.pageNumber}
                                </Typography>
                            ))}
                        </Box>
                    )}
                    <Tooltip
                        title={
                            hasInvalidFields
                                ? `Fix ${invalidFieldCount} invalid date${invalidFieldCount === 1 ? "" : "s"} before confirming`
                                : ""
                        }
                        disableHoverListener={!hasInvalidFields}
                    >
                        <span>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={onConfirmClick}
                                disabled={isSaving || hasInvalidFields}
                            >
                                Confirm Extraction
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
                {!isMobile && (
                    <Button
                        variant="outlined"
                        onClick={onViewAllPages}
                        size="small"
                    >
                        View Entire PDF
                    </Button>
                )}
                <Button
                    variant="outlined"
                    onClick={onSaveDraftInPlace}
                    size="small"
                    disabled={isSaving || !hasChanges}
                >
                    Save Draft
                </Button>
                <Button
                    variant="outlined"
                    color="warning"
                    onClick={onKickbackClick}
                    size="small"
                    disabled={isSaving}
                >
                    Send back to Identify
                </Button>
                <FormControl sx={{ minWidth: isMobile ? 140 : 200, width: "fit-content" }}>
                    <InputLabel>Form Type</InputLabel>
                    <Select
                        value={selectedFormType}
                        onChange={onFormTypeChange}
                        label="Form Type"
                        size="small"
                        MenuProps={{
                            PaperProps: { sx: { minWidth: "fit-content" } },
                        }}
                        sx={{
                            "& .MuiSelect-select": {
                                paddingRight: "32px !important",
                                whiteSpace: "nowrap",
                                overflow: "visible",
                                textOverflow: "clip",
                            },
                        }}
                        renderValue={(value) => {
                            if (!value) {
                                return <Box sx={{ whiteSpace: "nowrap" }}>All Forms</Box>;
                            }
                            const formType = value as string;
                            return (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
                                    {isFormFullyReviewed(formType) ? (
                                        <CheckIcon
                                            fontSize="small"
                                            color="success"
                                        />
                                    ) : (viewedPages[formType]?.size || 0) > 0 ? (
                                        <PanoramaFishEye
                                            fontSize="small"
                                            color="warning"
                                        />
                                    ) : (
                                        <RadioButtonUnchecked
                                            fontSize="small"
                                            color="disabled"
                                        />
                                    )}
                                    {formType.toUpperCase()}
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        ({viewedPages[formType]?.size || 0}/{formPageCounts[formType]}
                                        {removedPageCounts[formType] ? `, ${removedPageCounts[formType]} removed` : ""})
                                    </Typography>
                                    {unmatchedPageCounts[formType] > 0 && (
                                        <Typography
                                            variant="caption"
                                            color="warning.main"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {unmatchedPageCounts[formType]} unmatched
                                        </Typography>
                                    )}
                                </Box>
                            );
                        }}
                    >
                        <MenuItem value="">All Forms</MenuItem>
                        {availableFormTypes.map((formType) => (
                            <MenuItem
                                key={formType}
                                value={formType}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {isFormFullyReviewed(formType) ? (
                                        <CheckIcon
                                            fontSize="small"
                                            color="success"
                                        />
                                    ) : (viewedPages[formType]?.size || 0) > 0 ? (
                                        <PanoramaFishEye
                                            fontSize="small"
                                            color="warning"
                                        />
                                    ) : (
                                        <RadioButtonUnchecked
                                            fontSize="small"
                                            color="disabled"
                                        />
                                    )}
                                    {formType.toUpperCase()}
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        ({viewedPages[formType]?.size || 0}/{formPageCounts[formType]}
                                        {removedPageCounts[formType] ? `, ${removedPageCounts[formType]} removed` : ""})
                                    </Typography>
                                    {unmatchedPageCounts[formType] > 0 && (
                                        <Typography
                                            variant="caption"
                                            color="warning.main"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {unmatchedPageCounts[formType]} unmatched
                                        </Typography>
                                    )}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {isMobile && (
                    <ToggleButtonGroup
                        value={formOnly ? "form" : "pdf"}
                        exclusive
                        onChange={(_e, val) => {
                            if (val !== null) setFormOnly(val === "form");
                        }}
                        size="small"
                    >
                        <ToggleButton value="pdf">PDF</ToggleButton>
                        <ToggleButton value="form">Form</ToggleButton>
                    </ToggleButtonGroup>
                )}
                <Button
                    variant="outlined"
                    onClick={onBackClick}
                    size="small"
                >
                    Back to Tasks
                </Button>
            </Box>
        </Box>
    );
};

export default ExtractDataHeader;
